import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { useCallback } from "react";

import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { useMutationWithError } from "@/utils/hooks/useMutationWithError";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import { bookingSummaryAnalyticsDataAtom } from "../../BookingSummary/atoms/analytics/bookingSummaryAnalyticsDataAtom";
import {
  UpdateDiningBookingInput,
  useUpdateDiningBookingDataMutation,
} from "./__generated__/useUpdateDiningBookingDataMutation.graphql";

type CompleteArgs = {
  input: UpdateDiningBookingInput;
  onCompleted?: () => void;
  successToastMessage?: string;
};

export const useUpdateDiningBookingData = () => {
  const [commit, isUpdating] =
    useMutationWithError<useUpdateDiningBookingDataMutation>(
      useUpdateDiningBookingMutationString
    );
  const { showToast } = useToast();
  const analyticsData = useAtomValue(bookingSummaryAnalyticsDataAtom);
  const trackEvent = useTrackEvent();

  const updateDiningBooking = useCallback(
    ({ input, onCompleted, successToastMessage }: CompleteArgs) => {
      commit({
        variables: { input },
        onCompleted: () => {
          trackEvent("BookingDetail.MemberNotesUpdated", analyticsData ?? {});
          showToast({
            title: successToastMessage ?? t`Special requests updated`,
            semanticStyle: "success",
            toastId: "update-dining-booking-success",
          });
          onCompleted?.();
        },
      });
    },
    [analyticsData, commit, showToast, trackEvent]
  );

  return [updateDiningBooking, isUpdating] as const;
};

const useUpdateDiningBookingMutationString = graphql`
  mutation useUpdateDiningBookingDataMutation(
    $input: UpdateDiningBookingInput!
  ) {
    responsePayload: updateDiningBooking(input: $input) {
      success
      errors {
        code

        message
      }
      booking {
        ...DiningBookingSummary_booking
      }
    }
  }
`;
